import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { COLORS } from "../../../styles/uiConstants";
import { listHeadCellSx } from "./listStyles";

/**
 * Liste gövdesi: masaüstünde sticky-header tablo, mobilde kart listesi.
 * Scroll gövdenin İÇİNDE olur; toolbar ve footer sabit kalır.
 *
 * `scrollbarGutter: "stable"` — scrollbar belirip kaybolunca başlığın
 * kaymasını merkezî olarak engeller.
 *
 * İki gövde deseni: kolon-config (`columns`) veya satır-component (`renderRow`).
 */
export default function ListView({
  columns = [],
  renderRow,
  renderMobileCard,
  data = [],
  rowKey = "_id",
  rowSx,
  onRowClick,
  isLoading = false,
  loadingNode,
  empty,
  footer,
  bodyRef,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getKey = (row, i) =>
    typeof rowKey === "function" ? rowKey(row, i) : row?.[rowKey] ?? i;

  const isEmpty = !isLoading && data.length === 0;

  let body;
  if (isLoading) {
    body = loadingNode;
  } else if (isEmpty) {
    body = (
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {empty}
      </Box>
    );
  } else if (isMobile) {
    body = (
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {data.map((row, i) => (
          <React.Fragment key={getKey(row, i)}>
            {renderMobileCard?.(row, i)}
          </React.Fragment>
        ))}
      </Box>
    );
  } else {
    body = (
      <TableContainer sx={{ height: "100%", overflow: "auto", scrollbarGutter: "stable" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align}
                  // `width` opt-in bir kolon prop'udur ve BAŞLIK + GÖVDE hücresine
                  // birlikte uygulanır. Genişliği yalnız `cellSx`'e yazmak yetmiyordu:
                  // th'ye ulaşmadığı için tarayıcı sütunu içeriğe göre yeniden
                  // dağıtıyor, genişlik verilmeyen son sütun artan alanın tamamını
                  // yutuyordu (sağı bomboş görünen kolon). `cellSx.width` hâlâ
                  // çalışır — bu prop'u geçmeyen mevcut sayfalar etkilenmez.
                  sx={{ ...listHeadCellSx, ...(c.width ? { width: c.width } : {}), ...(c.headerSx || {}) }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderRow
              ? data.map((row, i) => (
                  <React.Fragment key={getKey(row, i)}>{renderRow(row, i)}</React.Fragment>
                ))
              : data.map((row, i) => (
                  <TableRow
                    key={getKey(row, i)}
                    hover
                    onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      ...(typeof rowSx === "function" ? rowSx(row, i) : rowSx || {}),
                    }}
                  >
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        sx={{ py: 1.25, ...(c.width ? { width: c.width } : {}), ...(c.cellSx || {}) }}
                      >
                        {c.render ? (
                          c.render(row, i)
                        ) : (
                          row?.[c.key] ?? (
                            <Box component="span" sx={{ color: COLORS.slate300 }}>
                              —
                            </Box>
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <Box
        ref={bodyRef}
        sx={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}
      >
        {body}
      </Box>
      {footer}
    </>
  );
}
